import { useMutation, useQueryClient } from "@tanstack/react-query"
import * as React from "react"
import { useTranslation } from "react-i18next"

import {
  customerQueries,
  PostCustomerApi,
  postCustomerApi,
  PutCustomerApi,
  putCustomerApi,
} from "@/clients/customers"
import { useSnackbarStore } from "@/store/snackbarStore"
import { toApiErrorMessage } from "@/clients/toApiErrorMessage"
import { CustomerModel } from "@/features/customers/models"
import { useBoltaEventEmitter } from "@/features/events"

import { CustomerFormValues } from "./CustomerForm.types"

export type UseCustomerFormSubmitProps = {
  workspaceId: number
  mode: "create" | "update"
  customerId?: number
  onSuccess?: (response: CustomerModel) => void
  onError?: (error: Error) => void
}

export const useCustomerFormSubmit = ({
  workspaceId,
  mode,
  customerId,
  onSuccess,
  onError,
}: UseCustomerFormSubmitProps) => {
  const emitter = useBoltaEventEmitter()

  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbarStore(state => state.openSnackbar)

  const { mutate: postCustomerMutate, isPending: isPendingPostCustomer } =
    useMutation({
      mutationFn: postCustomerApi,
    })

  const { mutate: putCustomerMutate, isPending: isPendingPutCustomer } = useMutation({
    mutationFn: putCustomerApi,
  })

  const upsertCustomer = React.useCallback(
    (data: CustomerFormValues) => {
      const commonOnSuccess = (response: CustomerModel) => {
        emitter.emit("clicked__submit__create_customer", {
          workspaceId,
        })
        queryClient.invalidateQueries(customerQueries.customers({ workspaceId }))
        queryClient.invalidateQueries(
          customerQueries.customerBy({
            workspaceId,
            customerId,
          }),
        )
        onSuccess?.(response)
      }

      const commonOnError = (error: Error) => {
        const message = toApiErrorMessage(t)(error)
        openSnackbar(message, "danger")
        onError?.(error)
      }

      if (mode === "create") {
        const createRequestBody = createPostRequestBody(data)
        postCustomerMutate(
          {
            ...createRequestBody,
            workspaceId,
          },
          {
            onSuccess: args => {
              openSnackbar(t("customer.created.success"), "success")
              commonOnSuccess(args)
            },
            onError: commonOnError,
          },
        )
      }

      if (mode === "update" && customerId) {
        const updateRequestBody = createPutRequestBody(data)
        putCustomerMutate(
          {
            ...updateRequestBody,
            workspaceId,
            customerId,
          },
          {
            onSuccess: args => {
              openSnackbar(t("customer.updated.success"), "success")
              commonOnSuccess(args)
            },
            onError: commonOnError,
          },
        )
      }
    },
    [
      customerId,
      mode,
      onError,
      onSuccess,
      openSnackbar,
      postCustomerMutate,
      putCustomerMutate,
      queryClient,
      t,
      workspaceId,
      emitter,
    ],
  )

  const isLoading = isPendingPostCustomer || isPendingPutCustomer
  return {
    upsertCustomer,
    isLoading,
  }
}

const createPostRequestBody = (
  data: CustomerFormValues,
): PostCustomerApi["RequestBody"] => {
  return {
    ...data,
    businessRegistrationNumber: data.businessRegistrationNumber || "",
    organizationName: data.organizationName || "",
    representativeName: data.representativeName || "",
    address: data.address,
    taxRegistrationId: data.taxRegistrationId,
    businessType: data.businessType,
    businessItem: data.businessItem,
    memo: data.memo,
    staffs: (data.staffs || []).map(staff => ({
      ...staff,
      useNotification: {
        email: staff.useNotification?.email ?? false,
        kakaoTalk: staff.useNotification?.kakaoTalk ?? false,
      },
    })),
  }
}

const createPutRequestBody = (
  data: CustomerFormValues,
): PutCustomerApi["RequestBody"] => {
  return {
    ...data,
    businessRegistrationNumber: data.businessRegistrationNumber || "",
    organizationName: data.organizationName || "",
    representativeName: data.representativeName || "",
    address: data.address,
    taxRegistrationId: data.taxRegistrationId,
    businessType: data.businessType,
    businessItem: data.businessItem,
    memo: data.memo,
    staffs: (data.staffs || []).map(staff => ({
      ...staff,
      useNotification: {
        email: staff.useNotification?.email ?? false,
        kakaoTalk: staff.useNotification?.kakaoTalk ?? false,
      },
    })),
  }
}
